import * as React from 'react';
import './NavBar.scss';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Tooltip from '@mui/material/Tooltip';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import Logo from 'assets/logo/LogoBoschBlanco.png';
import { Popover, Typography, Button, List, ListItemIcon, ListItemText, ListItemButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useState } from 'react';
import MenuAnonimo from '../MenuAnonimo/MenuAnonimo';
import { useError } from 'services/ErrorContext';
import { theme } from 'theme/theme';
import { IMenuItem } from 'utils/models/interfaces';
import MenuLogueado from '../MenuLogueado/MenuLogueado';
import { useAuth } from 'utils/hooks/useAuth';
import Alerta from '../Alerta.tsx/Alerta';
import { getAuthToken } from 'services/apiUtils';
import { pestanias } from 'utils/constants';
import { UsuarioDTO } from 'utils/models/types';
import { Mood, SentimentNeutral } from '@mui/icons-material';

const NavButton = ({ path, text, icon: Icon }: IMenuItem) => {
  const location = useLocation();
  const handleClick = () => {
    // Forzar la recarga de la página si la ruta actual es la misma que la del NavLink activo
    if (location.pathname === path) {
      window.location.reload();
    }
  };

  return (
    <>
      <NavLink to={path} end className={({ isActive }) => (isActive ? 'activeTab' : '')}>
        <Button
          className="menuItem"
          sx={{
            my: 2,
            color: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '700',
            fontSize: { md: '0.8rem', lg: '0.875rem' },
          }}
          onClick={handleClick}
        >
          {Icon && <Icon sx={{ fontSize: 16 }} />}
          {text}
        </Button>
      </NavLink>
    </>
  );
};

const NavBar = () => {
  const [openMenu, setOpenMenu] = useState<null | HTMLElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const usuarioJSON = localStorage.getItem('usuario');
  const usuario: UsuarioDTO = usuarioJSON && JSON.parse(usuarioJSON);
  const [mensajeLogout, setMensajeLogout] = useState(false);
  const navigate = useNavigate();
  const token = getAuthToken();
  const { logout } = useAuth();
  const { setError, clearError, setLoading } = useError();

  const onCerrarMenu = () => {
    setOpenMenu(null);
  };

  const onAbrirMenu = (e: React.MouseEvent<HTMLElement>) => {
    setOpenMenu(e.currentTarget);
    clearError();
  };

  const handleLogout = () => {
    setOpenMenu(null);
    setLoading(true);
    logout(setError, setLoading)
      .then(() => {
        navigate('/');
      })
      .catch(error => {
        setError('Error al cerrar sesión');
      })
      .finally(() => {
        setLoading(false);
        setMensajeLogout(true);
      });
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const popoverOpen = Boolean(anchorEl);
  const popoverId = popoverOpen ? 'simple-popover' : undefined;

  return (
    <AppBar className="header" sx={{ zIndex: 999999 }}>
      {/* HEADER CON LOGO E ÍCONO CUENTA */}
      <Alerta mensaje="Te has deslogueado con éxito" abierto={!!mensajeLogout} severity="success" />
      <Container className="top-bar">
        <NavLink to="/" end>
          <Tooltip title="Inicio">
            <Box height="90px" padding="10px 0" boxSizing="border-box">
              <img src={Logo} alt="logo teatro bosch" className="logo" />
            </Box>
          </Tooltip>
        </NavLink>
        <Box display="flex" alignItems="center" gap="10px">
          {token && (
            <Typography
              variant="h6"
              fontSize="14px"
              textOverflow="ellipsis"
              textAlign="left"
              color={theme.palette.secondary.main}
            >
              {`Hola, ${usuario?.Nombre}`}
            </Typography>
          )}
          <Tooltip title="Menú de usuario">
            <IconButton onClick={onAbrirMenu} sx={{ p: 0 }} aria-label="Menú de usuario">
              {token ? (
                <Mood sx={{ fontSize: '1.8rem', color: theme.palette.primary.contrastText }} />
              ) : (
                <SentimentNeutral sx={{ fontSize: '1.8rem', color: theme.palette.primary.contrastText }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        <Popover
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          anchorEl={openMenu}
          open={Boolean(openMenu)}
          onClose={onCerrarMenu}
          sx={{ mt: 2, zIndex: 999999999 }}
        >
          {token ? (
            <MenuLogueado open={!!openMenu} setOpen={setOpenMenu} usuario={usuario} handleLogout={handleLogout} />
          ) : (
            <Box width="320px">
              <MenuAnonimo setOpen={setOpenMenu} />
            </Box>
          )}
        </Popover>
      </Container>

      {/* NAVBAR MENU */}
      <Container
        maxWidth="xl"
        sx={{ color: theme.palette.primary.main, justifyContent: { xs: 'left', md: 'center' } }}
        className="nav-bar"
      >
        <Toolbar disableGutters>
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: '15px',
            }}
          >
            {pestanias.map(({ text, path, icon }: IMenuItem) => (
              <NavButton path={`${path}`} text={text} key={text} icon={icon} />
            ))}
          </Box>

          {/* MENU HAMBURGUESA PARA DISPOSITIVOS MÓVILES */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              aria-label="open popover"
              edge="start"
              onClick={handleOpenPopover}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Popover
              id={popoverId}
              open={popoverOpen}
              anchorEl={anchorEl}
              onClose={handleClosePopover}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <List>
                {pestanias.map(({ text, path, icon: Icon }: IMenuItem) => (
                  <ListItemButton key={text} component="a" href={path} onClick={handleClosePopover}>
                    <ListItemIcon>{Icon && <Icon sx={{ color: theme.palette.secondary.main }} />}</ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItemButton>
                ))}
              </List>
            </Popover>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default NavBar;
